import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faArrowRightLong,
  faFilePdf,
  faBolt,
  faChartPie,
  faScrewdriverWrench,
  faHandshake,
  faCalendarDays,
  faCircleCheck,
  faDoorOpen,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext.js";
import { generateJobAutomationPdf } from "../utils/jobAutomationPdf.js";

const round1 = (n) => Math.round(n * 10) / 10;
const round0 = (n) => Math.round(n);

function formatMoney(n, currency) {
  const val = Number.isFinite(n) ? n : 0;
  const cur = currency || "USD";
  const rounded = Math.round(val);
  return `${cur} ${rounded.toLocaleString()}`;
}

export default function JobAutomationReport() {
  const [reportCount, setReportCount] = useState(() => {
    return parseInt(localStorage.getItem("reports_generated") || "0");
  });
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [form, setForm] = useState({
    role: "",
    tasks: "",
    tools: "",
    hoursPerWeek: "",
    hourlyCost: "",
    currency: "USD",
    contactEmail: "",
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const runAnalysis = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!currentUser && reportCount >= 2) {
      setShowLoginPopup(true);
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_LLM_API_URL}/api/analyze-job`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: form.role,
            tasks: form.tasks,
            tools: form.tools,
            hoursPerWeek: form.hoursPerWeek,
            hourlyCost: form.hourlyCost,
            currency: form.currency,
            contactEmail: form.contactEmail,
          }),
        },
      );

      if (!res.ok) {
        throw new Error("Backend error");
      }

      const data = await res.json();
      setAnalysis(data);

      if (!currentUser) {
        const newCount = reportCount + 1;
        setReportCount(newCount);
        localStorage.setItem("reports_generated", newCount.toString());
      }
    } catch (err) {
      console.error(err);
      alert("Failed to analyze job");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!safeAnalysis) return;
    const logoUrl = `${process.env.PUBLIC_URL || ""}/logo.png`;
    await generateJobAutomationPdf(safeAnalysis, { logoUrl });
  };

  const cardStat = (label, value, icon) => (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
    </div>
  );

  const safeAnalysis = useMemo(() => {
    if (!analysis) return null;

    const taskTable = Array.isArray(analysis.taskTable)
      ? analysis.taskTable
      : [];

    const distribution = taskTable.reduce(
      (acc, t) => {
        acc[t.band] = (acc[t.band] || 0) + 1;
        return acc;
      },
      { High: 0, Medium: 0, Low: 0 },
    );

    const plan = analysis.recommendedAutomation.plan;

    return {
      ...analysis,
      taskTable,
      summary: {
        role: analysis.summary?.role || form.role,
        tasksCount: taskTable.length,
        overallAutomation: analysis.summary?.overallAutomation ?? 0,
        drivers: analysis.summary?.drivers || {
          repeatability: 0,
          decisionComplexity: 0,
          communicationLoad: 0,
          toolReadiness: 0,
        },
        distribution,
        savings: analysis.summary?.savings,
      },
      recommendedAutomation: {
        plan,
        toolSpecific: Array.isArray(
          analysis.recommendedAutomation?.toolSpecific,
        )
          ? analysis.recommendedAutomation.toolSpecific
          : [],
        riskNotes: Array.isArray(analysis.recommendedAutomation?.riskNotes)
          ? analysis.recommendedAutomation.riskNotes
          : [],
      },
      callToAction: {
        contactEmail: analysis.callToAction?.contactEmail || form.contactEmail,
        calendly: analysis.callToAction?.calendly || "",
        nextSteps: Array.isArray(analysis.callToAction?.nextSteps)
          ? analysis.callToAction.nextSteps
          : [
              "Select top 3 automatable tasks",
              "Design workflows",
              "Implement and measure ROI",
            ],
      },
    };
  }, [analysis, form]);

  return (
    <>
      <Helmet>
        <title>Job Automation Report</title>
      </Helmet>

      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: 'url("./assets/consul.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary opacity-80" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <FontAwesomeIcon icon={faRobot} />
              <span className="text-sm">
                Automation Readiness and Savings Report
              </span>
            </div>
            <h1 className="mt-5 text-4xl md:text-6xl font-extrabold leading-tight">
              Job Automation <span className="text-blue-200">Report</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200">
              Turn daily tasks into a clear automation plan: task-by-task
              automation %, estimated time savings, and recommended workflows.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          <form
            onSubmit={runAnalysis}
            className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="text-xl font-extrabold text-slate-900">
                Provide role, tasks, and tools
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Job Role
                </label>
                <input
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  placeholder="e.g., Operations Coordinator"
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Daily/Weekly Tasks (one per line)
                </label>
                <textarea
                  name="tasks"
                  value={form.tasks}
                  onChange={onChange}
                  placeholder={`Examples:
- Respond to customer emails and follow-ups
- Update CRM stages and notes
- Prepare weekly performance report
- Upload invoices and reconcile payments`}
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-44 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Tools You Use
                </label>
                <textarea
                  name="tools"
                  value={form.tools}
                  onChange={onChange}
                  placeholder="e.g., Gmail, Google Sheets, Slack, HubSpot CRM, Notion"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-28 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Hours / Week
                  </label>
                  <input
                    name="hoursPerWeek"
                    value={form.hoursPerWeek}
                    onChange={onChange}
                    placeholder="e.g., 40"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Hourly Cost
                  </label>
                  <input
                    name="hourlyCost"
                    value={form.hourlyCost}
                    onChange={onChange}
                    placeholder="e.g., 20"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="PKR">PKR</option>
                    <option value="SAR">SAR</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Contact Email
                </label>
                <input
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={onChange}
                  placeholder="hello@yourdomain.com"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-slate-400"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60"
              >
                {loading ? (
                  "Analyzing…"
                ) : (
                  <>
                    Generate Report <FontAwesomeIcon icon={faArrowRightLong} />
                  </>
                )}
              </button>

              <div className="text-xs text-slate-500 leading-relaxed">
                Tip: Add hours/week + hourly cost to unlock savings estimates.
                If you skip them, you still get automation % and
                recommendations.
              </div>
            </div>
          </form>

          <div className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
            {!currentUser && reportCount > 2 && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <FontAwesomeIcon
                  icon={faDoorOpen}
                  className="text-6xl mb-4 text-blue-600"
                />
                <div className="text-lg font-semibold">
                  Login to Generate a report
                </div>
                <div className="mt-2 text-sm max-w-md">
                  <button
                    onClick={() => navigate("/login")}
                    className="mt-2 w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60"
                  >
                    Login
                  </button>
                </div>
              </div>
            )}
            {!safeAnalysis && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <FontAwesomeIcon
                  icon={faRobot}
                  className="text-6xl mb-4 text-blue-600"
                />
                <div className="text-lg font-semibold">
                  Your report appears here
                </div>
                <div className="mt-2 text-sm max-w-md">
                  Enter your role and tasks to generate a structured report
                  including task-by-task automation potential, estimated
                  savings, and a recommended automation plan.
                </div>
              </div>
            )}

            {safeAnalysis && (
              <div className="space-y-10">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-sm text-slate-500">
                      Overall automation potential
                    </div>
                    <div className="text-6xl font-extrabold text-secondary">
                      {round0(safeAnalysis.summary.overallAutomation)}%
                    </div>
                    <div className="mt-2 text-slate-600">
                      Tasks analyzed:{" "}
                      <span className="font-semibold">
                        {safeAnalysis.summary.tasksCount}
                      </span>
                      {safeAnalysis.summary.role ? (
                        <>
                          {" "}
                          , Role:{" "}
                          <span className="font-semibold">
                            {safeAnalysis.summary.role}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={downloadPDF}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Download PDF
                    </button>
                    <a
                      href={safeAnalysis.callToAction.calendly || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Book a Call
                    </a>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  {cardStat(
                    "Repeatability",
                    `${round0(safeAnalysis.summary.drivers.repeatability)}%`,
                    faBolt,
                  )}
                  {cardStat(
                    "Decision Complexity",
                    `${round0(safeAnalysis.summary.drivers.decisionComplexity)}%`,
                    faChartPie,
                  )}
                  {cardStat(
                    "Communication Load",
                    `${round0(safeAnalysis.summary.drivers.communicationLoad)}%`,
                    faHandshake,
                  )}
                  {cardStat(
                    "Tool Readiness",
                    `${round0(safeAnalysis.summary.drivers.toolReadiness)}%`,
                    faScrewdriverWrench,
                  )}
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 font-extrabold text-slate-900">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-green-600"
                    />
                    Summary of tasks and their percentage of automation
                  </div>
                  <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                    The percentages below represent estimated automation
                    potential for each task. High does not mean “remove humans”,
                    it means you can automate execution while keeping human
                    approval where needed.
                  </div>
                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">High</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.distribution.High}
                      </div>
                      <div className="text-xs text-slate-500">
                        Mostly automatable
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Medium</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.distribution.Medium}
                      </div>
                      <div className="text-xs text-slate-500">
                        Partial automation
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Low</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.distribution.Low}
                      </div>
                      <div className="text-xs text-slate-500">
                        Keep core manual
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">
                    Overall estimated savings
                  </div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    Savings are estimated from your hours/week and hourly cost.
                    If you did not provide them, fill them in and regenerate.
                  </div>

                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">
                        Hours saved / week
                      </div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0
                          ? `${round1(safeAnalysis.summary.savings.weeklyHoursSaved)}`
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">
                        Value saved / month
                      </div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0 &&
                        safeAnalysis.summary.savings.hourlyCost > 0
                          ? formatMoney(
                              safeAnalysis.summary.savings.monthlyValue,
                              safeAnalysis.summary.savings.currency,
                            )
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">
                        Value saved / year
                      </div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0 &&
                        safeAnalysis.summary.savings.hourlyCost > 0
                          ? formatMoney(
                              safeAnalysis.summary.savings.yearlyValue,
                              safeAnalysis.summary.savings.currency,
                            )
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">
                    Task with percentage of potential automation
                  </div>
                  <div className="mt-3 space-y-3">
                    {safeAnalysis.taskTable.slice(0, 20).map((t, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-xl bg-slate-50 border border-slate-200"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="font-semibold text-slate-900">
                            {t.task}
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-lg font-extrabold text-secondary">
                              {t.automationPotential}%
                            </div>
                            <div className="text-xs text-slate-500">
                              {t.band}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          Recommended automation:{" "}
                          <span className="font-semibold">
                            {t.recommendedAutomation}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Estimated savings (relative): {t.estSavingsPct}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">
                    Recommended automation
                  </div>
                  <div className="mt-3 space-y-4">
                    {safeAnalysis.recommendedAutomation.plan.map((p, idx) => (
                      <div
                        key={idx}
                        className="p-5 rounded-2xl bg-slate-50 border border-slate-200"
                      >
                        <div className="text-lg font-extrabold text-slate-900">
                          {p.title}
                        </div>
                        <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                          <span className="font-semibold">Why:</span> {p.why}
                        </div>
                        <div className="mt-3 text-sm text-slate-700 font-semibold">
                          What to implement
                        </div>
                        <div className="mt-2 space-y-2">
                          {p.what.map((w, i) => (
                            <div key={i} className="text-sm text-slate-600">
                              • {w}
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 text-sm text-slate-600">
                          <span className="font-semibold">Best for:</span>{" "}
                          {p.bestFor}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">
                      Estimated Savings
                    </div>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                      Your overall estimated savings depends on workload and
                      hourly cost. If you provided them, the report already
                      calculated weekly/monthly/yearly value. Practical
                      expectation: start with top 3 tasks, automate, measure for
                      2 weeks, then scale.
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">
                      Tool-specific opportunities
                    </div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.recommendedAutomation.toolSpecific
                        .length ? (
                        safeAnalysis.recommendedAutomation.toolSpecific.map(
                          (t, i) => (
                            <div key={i} className="text-sm text-slate-600">
                              • {t}
                            </div>
                          ),
                        )
                      ) : (
                        <div className="text-sm text-slate-600">
                          • No specific tools detected. Add tools and
                          regenerate.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">
                      Risks and guardrails
                    </div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.recommendedAutomation.riskNotes.map(
                        (r, i) => (
                          <div key={i} className="text-sm text-slate-600">
                            • {r}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-900 text-white">
                  <div className="mt-2 text-slate-200 text-lg font-semibold">
                    Want this converted into real automations (workflows, AI
                    assistant, dashboards) with measurable ROI?
                  </div>

                  <div className="mt-5 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => window.openCalendly?.()}
                      className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Book via Calendly
                    </button>
                  </div>

                  <div className="mt-6">
                    <div className="text-sm font-semibold text-white/90">
                      Next steps
                    </div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.callToAction.nextSteps.map((ns, i) => (
                        <div key={i} className="text-sm text-slate-200">
                          • {ns}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 text-xs text-slate-300">
                    PDF includes the same structure so it can be shared
                    internally or sent to stakeholders.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {showLoginPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faRobot} size="2x" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Limit Reached</h3>
            <p className="mt-3 text-slate-600">
              You've generated {reportCount} free reports! Please sign in to
              continue generating unlimited automation insights and saving your
              history.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => navigate("/login")}
                className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                Login / Sign Up
              </button>
              <button
                onClick={() => setShowLoginPopup(false)}
                className="text-sm text-slate-400 hover:text-slate-600 font-medium"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
